import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, Check, Image as ImageIcon, Video, Music, Code, PenTool, Palette, Zap, Search, Sparkles, UploadCloud } from 'lucide-react';
import { GlassCard } from './ui/GlassCard';
import { supabase } from '../lib/supabase';
import { PricingTier } from '../types/database';
interface SubmitToolModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const CATEGORIES = [{
  name: 'Image',
  icon: ImageIcon
}, {
  name: 'Video',
  icon: Video
}, {
  name: 'Audio',
  icon: Music
}, {
  name: 'Development',
  icon: Code
}, {
  name: 'Writing',
  icon: PenTool
}, {
  name: 'Design',
  icon: Palette
}, {
  name: 'Productivity',
  icon: Zap
}, {
  name: 'Research',
  icon: Search
}, {
  name: 'Other',
  icon: Sparkles
}];
export function SubmitToolModal({
  isOpen,
  onClose
}: SubmitToolModalProps) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [useUrlInput, setUseUrlInput] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website_url: '',
    category: 'Productivity',
    submitter_email: '',
    logo_url: ''
  });
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([{
    name: 'Free',
    price: '0',
    features: ['Basic features']
  }]);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setStep(1);
      setPreviewUrl(null);
      setFormData({
        name: '',
        description: '',
        website_url: '',
        category: 'Productivity',
        submitter_email: '',
        logo_url: ''
      });
      setPricingTiers([{
        name: 'Free',
        price: '0',
        features: ['Basic features']
      }]);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };
  const handleFile = async (file: File) => {
    if (!file.type.match('image.*')) {
      alert('Please upload an image file (JPG, PNG, SVG)');
      return;
    }
    // Create preview URL for display
    const previewUrl = URL.createObjectURL(file);
    setPreviewUrl(previewUrl);
    // Try to upload to Supabase Storage
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `tool-logos/${fileName}`;
      console.log('🔄 Uploading file to Supabase Storage...', filePath);
      const {
        error: uploadError,
        data
      } = await supabase.storage.from('faind-images').upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });
      if (uploadError) {
        console.error('❌ Upload error:', uploadError);
        console.error('❌ Error details:', {
          message: uploadError.message,
          statusCode: (uploadError as any).statusCode,
          error: (uploadError as any).error
        });
        // Check if bucket doesn't exist
        if (uploadError.message.includes('Bucket not found') || uploadError.message.includes('not found')) {
          alert('⚠️ Storage bucket not found. Please check that "faind-images" bucket exists in Supabase Dashboard.\n\nFor now, you can paste an image URL instead.');
          setPreviewUrl(null);
          setFormData({
            ...formData,
            logo_url: ''
          });
          return;
        }
        // Check for permission errors
        if (uploadError.message.includes('permission') || uploadError.message.includes('policy') || (uploadError as any).statusCode === 403) {
          alert('⚠️ Permission denied. Please run the storage policies SQL script.\n\nFor now, you can paste an image URL instead.');
          setPreviewUrl(null);
          setFormData({
            ...formData,
            logo_url: ''
          });
          return;
        }
        alert(`Failed to upload logo: ${uploadError.message}\n\nPlease try the "Paste URL" option instead.`);
        setPreviewUrl(null);
        setFormData({
          ...formData,
          logo_url: ''
        });
        return;
      }
      console.log('✅ Upload successful:', data);
      // Get public URL
      const {
        data: {
          publicUrl
        }
      } = supabase.storage.from('faind-images').getPublicUrl(filePath);
      console.log('✅ Public URL generated:', publicUrl);
      // Update form data with the public URL
      setFormData({
        ...formData,
        logo_url: publicUrl
      });
    } catch (error: any) {
      console.error('❌ Error uploading file:', error);
      alert(`Failed to upload logo: ${error.message || 'Unknown error'}. Please try again.`);
      setPreviewUrl(null);
      setFormData({
        ...formData,
        logo_url: ''
      });
    }
  };
  const removeFile = () => {
    setPreviewUrl(null);
    setFormData({
      ...formData,
      logo_url: ''
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const addPricingTier = () => {
    setPricingTiers([...pricingTiers, {
      name: '',
      price: '',
      features: ['']
    }]);
  };
  const removePricingTier = (index: number) => {
    setPricingTiers(pricingTiers.filter((_, i) => i !== index));
  };
  const updatePricingTier = (index: number, field: keyof PricingTier, value: any) => {
    const newTiers = [...pricingTiers];
    newTiers[index] = {
      ...newTiers[index],
      [field]: value
    };
    setPricingTiers(newTiers);
  };
  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.name || !formData.description || !formData.website_url || !formData.submitter_email) {
      alert('Please fill in all required fields');
      return;
    }
    // Logo is optional if storage isn't set up
    if (!formData.logo_url) {
      const proceed = confirm('No logo provided. Continue without a logo?');
      if (!proceed) return;
    }
    setIsSubmitting(true);
    try {
      const {
        error
      } = await supabase.from('submissions').insert({
        ...formData,
        pricing_tiers: pricingTiers,
        status: 'pending'
      });
      if (error) throw error;
      setStep(3);
    } catch (err) {
      console.error('Error submitting tool:', err);
      alert('Failed to submit tool. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const SelectedCategoryIcon = CATEGORIES.find(c => c.name === formData.category)?.icon || Sparkles;
  if (!isOpen) return null;
  const modalContent = <AnimatePresence data-id="element-457">
      <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" data-id="element-458">
        {/* Dark Blurred Overlay */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/90 backdrop-blur-xl" onClick={onClose} data-id="element-459" />

        {/* Modal Content */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-460">
          <GlassCard className="relative border-white/10" data-id="element-461">
            <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors z-10" data-id="element-462">
              <X size={20} data-id="element-463" />
            </button>

            <div className="p-8" data-id="element-464">
              {step === 1 && <div className="space-y-6" data-id="element-465">
                  <div data-id="element-466">
                    <h2 className="text-3xl font-bold text-white mb-2 font-special" data-id="element-467">
                      Submit a Tool
                    </h2>
                    <p className="text-gray-400" data-id="element-468">
                      Share your AI tool with our growing community.
                    </p>
                  </div>

                  <div className="space-y-5" data-id="element-469">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-470">
                      <div data-id="element-471">
                        <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-472">
                          Tool Name
                        </label>
                        <input type="text" name="name" value={formData.name} onChange={handleInputChange} required className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 focus:outline-none transition-all" placeholder="e.g. ChatGPT" data-id="element-473" />
                      </div>
                      <div data-id="element-474">
                        <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-475">
                          Website URL
                        </label>
                        <input type="url" name="website_url" value={formData.website_url} onChange={handleInputChange} required className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 focus:outline-none transition-all" placeholder="https://..." data-id="element-476" />
                      </div>
                    </div>

                    <div data-id="element-477">
                      <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-478">
                        Description
                      </label>
                      <textarea name="description" value={formData.description} onChange={handleInputChange} required rows={3} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 focus:outline-none transition-all resize-none" placeholder="What does this tool do?" data-id="element-479" />
                    </div>

                    <div data-id="element-480">
                      <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-481">
                        Category
                      </label>
                      <div className="relative" data-id="element-482">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-400 pointer-events-none" data-id="element-483">
                          <SelectedCategoryIcon size={18} data-id="element-484" />
                        </div>
                        <select name="category" value={formData.category} onChange={handleInputChange} required className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 focus:outline-none transition-all appearance-none cursor-pointer" data-id="element-485">
                          {CATEGORIES.map(cat => <option key={cat.name} value={cat.name} className="bg-gray-900 text-white" data-id="element-486">
                              {cat.name}
                            </option>)}
                        </select>
                      </div>
                    </div>

                    <div data-id="element-487">
                      <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-488">
                        Logo
                      </label>

                      {/* Toggle between upload and URL input */}
                      <div className="flex gap-2 mb-3" data-id="element-489">
                        <button type="button" onClick={() => setUseUrlInput(false)} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${!useUrlInput ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`} data-id="element-490">
                          Upload File
                        </button>
                        <button type="button" onClick={() => {
                      setUseUrlInput(true);
                      setPreviewUrl(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${useUrlInput ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`} data-id="element-491">
                          Paste URL
                        </button>
                      </div>

                      {useUrlInput ?
                  // URL Input Mode
                  <input type="url" name="logo_url" value={formData.logo_url} onChange={e => {
                    handleInputChange(e);
                    if (e.target.value) {
                      setPreviewUrl(e.target.value);
                    } else {
                      setPreviewUrl(null);
                    }
                  }} placeholder="https://example.com/logo.png" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 focus:outline-none transition-all" data-id="element-492" /> :
                  // File Upload Mode
                  <div className={`relative border-2 border-dashed rounded-xl p-6 transition-all text-center cursor-pointer ${dragActive ? 'border-cyan-500 bg-cyan-500/10' : 'border-white/10 bg-white/5 hover:border-white/20'}`} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} onClick={() => !previewUrl && fileInputRef.current?.click()} data-id="element-493">
                          <input ref={fileInputRef} type="file" className="hidden" accept="image/png, image/jpeg, image/svg+xml" onChange={handleFileSelect} data-id="element-494" />

                          {previewUrl ? <div className="relative flex items-center justify-center" data-id="element-495">
                              <div className="w-20 h-20 rounded-xl overflow-hidden bg-black/20 border border-white/10" data-id="element-496">
                                <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" data-id="element-497" />
                              </div>
                              <button onClick={e => {
                        e.stopPropagation();
                        removeFile();
                      }} className="absolute -top-2 -right-2 p-1.5 rounded-full bg-red-500 text-white hover:bg-red-600" data-id="element-498">
                                <Trash2 size={14} data-id="element-499" />
                              </button>
                            </div> : <div className="flex flex-col items-center gap-2" data-id="element-500">
                              <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center" data-id="element-501">
                                <UploadCloud size={24} className="text-gray-400" data-id="element-502" />
                              </div>
                              <p className="text-sm font-medium text-white" data-id="element-503">
                                Click to upload or drag and drop
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-504">
                                SVG, PNG, JPG (max 5MB)
                              </p>
                            </div>}
                        </div>}

                      {previewUrl && useUrlInput && <div className="mt-3 flex items-center justify-center" data-id="element-505">
                          <div className="w-20 h-20 rounded-xl overflow-hidden bg-black/20 border border-white/10" data-id="element-506">
                            <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" onError={() => {
                        setPreviewUrl(null);
                        alert('Invalid image URL. Please check the URL and try again.');
                      }} data-id="element-507" />
                          </div>
                        </div>}
                    </div>

                    <div data-id="element-508">
                      <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-509">
                        Your Email
                      </label>
                      <input type="email" name="submitter_email" value={formData.submitter_email} onChange={handleInputChange} required className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 focus:outline-none transition-all" placeholder="For approval notifications" data-id="element-510" />
                    </div>

                    <div className="pt-4 flex justify-end" data-id="element-511">
                      <button onClick={() => setStep(2)} className="px-8 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-bold hover:opacity-90 transition-all" data-id="element-512">
                        Next: Pricing
                      </button>
                    </div>
                  </div>
                </div>}

              {step === 2 && <div className="space-y-6" data-id="element-513">
                  <div data-id="element-514">
                    <h2 className="text-3xl font-bold text-white mb-2 font-special" data-id="element-515">
                      Pricing Tiers
                    </h2>
                    <p className="text-gray-400" data-id="element-516">
                      How much does your tool cost?
                    </p>
                  </div>

                  <div className="space-y-4" data-id="element-517">
                    {pricingTiers.map((tier, index) => <div key={index} className="p-4 rounded-xl bg-white/5 border border-white/10 relative" data-id="element-518">
                        <button onClick={() => removePricingTier(index)} className="absolute top-2 right-2 p-2 text-gray-500 hover:text-red-400" data-id="element-519">
                          <Trash2 size={16} data-id="element-520" />
                        </button>

                        <div className="grid grid-cols-2 gap-4" data-id="element-521">
                          <div data-id="element-522">
                            <label className="block text-xs text-gray-400 mb-1" data-id="element-523">
                              Plan Name
                            </label>
                            <input type="text" value={tier.name} onChange={e => updatePricingTier(index, 'name', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none" placeholder="e.g. Pro" data-id="element-524" />
                          </div>
                          <div data-id="element-525">
                            <label className="block text-xs text-gray-400 mb-1" data-id="element-526">
                              Price
                            </label>
                            <input type="text" value={tier.price} onChange={e => updatePricingTier(index, 'price', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none" placeholder="e.g. $10/mo" data-id="element-527" />
                          </div>
                        </div>
                      </div>)}

                    <button onClick={addPricingTier} className="w-full py-4 rounded-xl border border-dashed border-white/20 text-gray-400 hover:text-white hover:border-cyan-500/50 transition-all flex items-center justify-center gap-2" data-id="element-528">
                      <Plus size={16} data-id="element-529" />
                      <span data-id="element-530">Add Another Tier</span>
                    </button>
                  </div>

                  <div className="flex justify-between pt-4 border-t border-white/10" data-id="element-531">
                    <button onClick={() => setStep(1)} className="px-6 py-3 rounded-xl text-gray-400 hover:text-white hover:bg-white/5 transition-all" data-id="element-532">
                      Back
                    </button>
                    <button onClick={handleSubmit} disabled={isSubmitting} className="px-8 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-bold hover:opacity-90 transition-all disabled:opacity-50 flex items-center gap-2" data-id="element-533">
                      {isSubmitting ? <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-534" />
                          <span data-id="element-535">Submitting...</span>
                        </> : <>
                          <span data-id="element-536">Submit Tool</span>
                          <Check size={18} data-id="element-537" />
                        </>}
                    </button>
                  </div>
                </div>}

              {step === 3 && <div className="text-center py-12" data-id="element-538">
                  <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-green-500/30" data-id="element-539">
                    <Check size={48} className="text-green-400" data-id="element-540" />
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-3 font-special" data-id="element-541">
                    Submission Received!
                  </h2>
                  <p className="text-gray-400 mb-8" data-id="element-542">
                    Thanks for submitting{' '}
                    <span className="text-white font-bold" data-id="element-543">
                      {formData.name}
                    </span>
                    . We'll review it shortly and email you at{' '}
                    <span className="text-cyan-400" data-id="element-544">
                      {formData.submitter_email}
                    </span>
                    .
                  </p>
                  <button onClick={onClose} className="px-10 py-3 rounded-xl bg-white text-black font-bold hover:bg-gray-200 transition-all" data-id="element-545">
                    Close
                  </button>
                </div>}
            </div>
          </GlassCard>
        </motion.div>
      </div>
    </AnimatePresence>;
  return createPortal(modalContent, document.body);
}